import { Box, Typography } from '@mui/material';
import { JSX, ReactNode } from 'react';

interface PageHeaderSummaryItemProps {
  /**
   * Title of the item to display
   */
  title: string;
  /**
   * Value of the item to display
   */
  value: ReactNode;
}

/**
 * Summary item to display under the title
 */
const PageHeaderSummaryItem = ({ title, value }: PageHeaderSummaryItemProps): JSX.Element => (
  <Box className="flex space-x-2">
    <Typography>{title}:</Typography>
    <Typography fontWeight="bold">{value ?? '-'}</Typography>
  </Box>
);

export interface PageHeaderSummaryProps {
  /**
   * Title of the page
   */
  title: string;
  /**
   * Title action to display next to the title
   */
  titleAction?: ReactNode;
  /**
   * Items to display under the title
   */
  items: PageHeaderSummaryItemProps[];
}

/**
 * Component for displaying a summary of the page
 */
const PageHeaderSummary = ({ title, titleAction, items }: PageHeaderSummaryProps): JSX.Element => (
  <Box>
    <Box className="flex items-center space-x-6">
      <Typography variant="h2">{title}</Typography>
      {titleAction}
    </Box>
    <Box className="inline-grid md:grid-cols-2 gap-x-4 gap-y-1 mt-2">
      {items.map((item, index) => (
        <Box
          key={item.title}
          borderRight={index % 2 === 0 ? 1 : 0}
          borderColor="common.borderGray"
          className="pr-2"
        >
          <PageHeaderSummaryItem {...item} />
        </Box>
      ))}
    </Box>
  </Box>
);

export default PageHeaderSummary;
